import { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { useTheme } from '../../hooks/useTheme';
import { COLORS, FONTS } from '../../styles/tokens';
import {
  fetchUsers, fetchRealmRoles, fetchUserRoles, assignRoles, removeUserRole,
  type KeycloakUser, type KeycloakRole,
} from '../../api/keycloak-admin';
import { recordAccessEvent } from '../../lib/notifications-api';
import type { AppDetailContext } from './app-detail-context';

/** Parse the comma-separated vertex.elevatedRoles client attribute. */
function parseElevatedRoles(attr: string | undefined): string[] {
  return (attr ?? '').split(',').map(s => s.trim()).filter(Boolean);
}

/**
 * Per-app access management. Lists realm users and lets an admin toggle the
 * app's access role (app:<clientId>) and any elevated roles the app declares
 * via its `vertex.elevatedRoles` attribute. Writes go through the existing
 * admin proxy; each change is recorded in the audit feed.
 */
export default function AppAccess(): React.JSX.Element {
  const { client } = useOutletContext<AppDetailContext>();
  const { token } = useAuth();
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;

  const accessRoleName = `app:${client.clientId}`;
  const elevatedRoleNames = useMemo(
    () => parseElevatedRoles(client.attributes?.['vertex.elevatedRoles']),
    [client.attributes],
  );
  const managedRoleNames = useMemo(
    () => [accessRoleName, ...elevatedRoleNames],
    [accessRoleName, elevatedRoleNames],
  );

  const [users, setUsers] = useState<KeycloakUser[]>([]);
  const [roleByName, setRoleByName] = useState<Record<string, KeycloakRole>>({});
  const [userRoles, setUserRoles] = useState<Record<string, Set<string>>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [allUsers, realmRoles] = await Promise.all([fetchUsers(token), fetchRealmRoles(token)]);
      const roster = allUsers.filter(u => !u.username.startsWith('service-account-'));
      const byName: Record<string, KeycloakRole> = {};
      for (const r of realmRoles) byName[r.name] = r;

      const roleMap: Record<string, Set<string>> = {};
      await Promise.all(roster.map(async (u) => {
        const roles = await fetchUserRoles(token, u.id);
        roleMap[u.id] = new Set(roles.map(r => r.name));
      }));

      setUsers(roster);
      setRoleByName(byName);
      setUserRoles(roleMap);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  /** Roles that the app declares but that do not exist in the realm. */
  const missingRoles = managedRoleNames.filter(name => !loading && !roleByName[name]);

  async function toggle(user: KeycloakUser, roleName: string): Promise<void> {
    const role = roleByName[roleName];
    if (!role) return;
    const key = `${user.id}:${roleName}`;
    const has = userRoles[user.id]?.has(roleName) ?? false;
    setBusyKey(key);
    setError(null);
    try {
      if (has) {
        await removeUserRole(token, user.id, [role]);
      } else {
        await assignRoles(token, user.id, [role]);
      }
      await recordAccessEvent(token, {
        action: has ? 'revoke' : 'grant',
        appSlug: client.clientId,
        roleName,
        targetUserId: user.id,
        targetLabel: user.username || user.email,
      });
      setUserRoles(prev => {
        const next = new Set(prev[user.id] ?? []);
        if (has) next.delete(roleName); else next.add(roleName);
        return { ...prev, [user.id]: next };
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyKey(null);
    }
  }

  const filtered = users.filter(u => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return u.username.toLowerCase().includes(q)
      || u.email?.toLowerCase().includes(q)
      || `${u.firstName} ${u.lastName}`.toLowerCase().includes(q);
  });

  if (loading) {
    return <p style={{ fontFamily: FONTS.body, color: colors.textSecondary }}>Loading access...</p>;
  }

  const availableRoleColumns = managedRoleNames.filter(name => roleByName[name]);

  return (
    <div>
      <p style={{ fontFamily: FONTS.body, fontSize: '13px', color: colors.textSecondary, margin: '0 0 16px' }}>
        Grant or revoke access to <strong style={{ color: colors.text }}>{client.name || client.clientId}</strong> and
        its elevated roles. Changes apply on the user's next fresh token and are recorded in the audit log.
      </p>

      {error && <Banner tone="error" text={error} />}
      {missingRoles.length > 0 && (
        <Banner
          tone="warn"
          text={`These declared roles do not exist in the realm and cannot be assigned: ${missingRoles.join(', ')}. Create them in the realm first.`}
        />
      )}

      <input
        className="vx-search-field"
        placeholder="Search users..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{
          height: '34px', padding: '0 12px', borderRadius: '6px', width: '260px',
          border: `1px solid ${colors.border}`,
          backgroundColor: isDark ? COLORS.dark.cardAlt : COLORS.light.cardAlt,
          color: colors.text, fontFamily: FONTS.body, fontSize: '13px', outline: 'none',
          marginBottom: '12px',
        }}
      />

      <div className="vx-table-wrap" style={{
        border: `1px solid ${colors.border}`, borderRadius: '10px', overflowX: 'auto',
        backgroundColor: isDark ? COLORS.dark.card : COLORS.light.card,
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <Th colors={colors}>User</Th>
              {availableRoleColumns.map(name => (
                <Th key={name} colors={colors} center>
                  {name === accessRoleName ? 'Access' : name}
                </Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} style={{ borderTop: `1px solid ${colors.border}` }}>
                <td style={{ padding: '10px 14px' }}>
                  <div style={{ fontFamily: FONTS.body, fontSize: '13px', color: colors.text }}>
                    {`${u.firstName} ${u.lastName}`.trim() || u.username}
                  </div>
                  <div style={{ fontFamily: FONTS.body, fontSize: '11px', color: colors.textSecondary }}>
                    {u.email || u.username}
                  </div>
                </td>
                {availableRoleColumns.map(name => {
                  const key = `${u.id}:${name}`;
                  return (
                    <td key={name} style={{ padding: '10px 14px', textAlign: 'center' }}>
                      <Toggle
                        on={userRoles[u.id]?.has(name) ?? false}
                        busy={busyKey === key}
                        onClick={() => toggle(u, name)}
                        isDark={isDark}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={availableRoleColumns.length + 1} style={{
                  padding: '20px', textAlign: 'center',
                  fontFamily: FONTS.body, fontSize: '13px', color: colors.textSecondary,
                }}>
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Table header cell */
function Th({ children, colors, center }: {
  children: React.ReactNode; colors: { textSecondary: string }; center?: boolean;
}): React.JSX.Element {
  return (
    <th style={{
      padding: '10px 14px', textAlign: center ? 'center' : 'left',
      fontFamily: FONTS.heading, fontSize: '10px', letterSpacing: '0.12em',
      textTransform: 'uppercase', color: colors.textSecondary, fontWeight: 600,
    }}>
      {children}
    </th>
  );
}

/** Small on/off switch */
function Toggle({ on, busy, onClick, isDark }: {
  on: boolean; busy: boolean; onClick: () => void; isDark: boolean;
}): React.JSX.Element {
  return (
    <button
      onClick={onClick}
      disabled={busy}
      aria-pressed={on}
      style={{
        position: 'relative', width: '38px', height: '22px', borderRadius: '11px',
        border: 'none', cursor: busy ? 'wait' : 'pointer', padding: 0,
        opacity: busy ? 0.6 : 1,
        backgroundColor: on
          ? (isDark ? COLORS.accent.blueDark : COLORS.accent.blue)
          : (isDark ? '#3a3a3f' : '#cccccc'),
        transition: 'background-color 0.2s',
      }}
    >
      <span style={{
        position: 'absolute', top: '2px', left: on ? '18px' : '2px',
        width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#FFFFFF',
        transition: 'left 0.2s',
      }} />
    </button>
  );
}

/** Inline banner for errors/warnings */
function Banner({ tone, text }: { tone: 'error' | 'warn'; text: string }): React.JSX.Element {
  const color = tone === 'error' ? '#EF4444' : '#F59E0B';
  return (
    <div style={{
      border: `1px solid ${color}`, borderRadius: '8px', padding: '10px 14px', marginBottom: '12px',
      backgroundColor: tone === 'error' ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.08)',
      fontFamily: FONTS.body, fontSize: '12px', color,
    }}>
      {text}
    </div>
  );
}
